import React, { Suspense, useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import isPropValid from '@emotion/is-prop-valid';
import { StyleSheetManager, ThemeProvider } from 'styled-components';
import theme from '@app/ui/theme';
import {
  createHashRouter,
  RouterProvider,
  createRoutesFromElements,
  Route,
  useLocation,
  useNavigate
} from 'react-router';
import { QueryClient, QueryCache, QueryClientProvider } from '@tanstack/react-query';
import { setFutureSecrets } from '@app/stores/futureSecretsStore';
import LandingPage from './views/LandingPage/LandingPage';
import { PlannerSkeleton } from './components/PlannerSkeleton';
import { useIsReady } from '@app/stores/initializationStore';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import WebVitalsReporter from '@app/components/WebVitalsReporter';
import { getLandingPageConfig } from '@app/rangeConfig/landingPageConfig';
import { e2e } from '@inter-ikea-storageone/shared/utils/queryUtils';
import { isSpaceEnabled } from '@inter-ikea-storageone/shared/utils/integration';
import { AppRoutes } from '@app/routes';
import { ServiceSettingsProvider } from '@app/contexts/serviceSettings';
import I18NProvider from '@app/i18n/I18NProvider';
import VirtualKeyboardProvider from '@app/contexts/virtualKeyboard';
import {
  extractAndStoreToken,
  sanitizeUrl
} from '@inter-ikea-storageone/shared/utils/proxyTokenUtils';
import { ServicesProvider } from '@app/contexts/services';
import AppHelmet from '@app/components/Helmet/Helmet';

// CRITICAL: Pre-load landing page config AND images BEFORE React renders
// This starts image downloads immediately, preventing waterfall delays

getLandingPageConfig()
  .then(config => {
    // Preload critical images by creating Image objects
    // This starts the download before React even mounts
    const imageUrls = [
      config.rangeDesign?.backgroundImgUrl,
      config.designFromScratch?.backgroundImgUrl,
      config.openDesign?.backgroundImgUrl
    ].filter(Boolean);

    imageUrls.forEach(url => {
      const img = new Image();
      img.src = url as string;
    });
  })
  .catch(() => {
    // Ignore errors - config will be fetched again by useLandingPageConfig hook
  });

extractAndStoreToken();
sanitizeUrl();
window.addEventListener('popstate', sanitizeUrl);

// Lazy load the app (only when navigating to planner routes)
const Planner = React.lazy(() => import('./Planner'));

const privateApiKey = import.meta.env.VITE_FUTURE_PRIVATE_API_KEY;
const bearerToken = import.meta.env.VITE_FUTURE_BEARER_TOKEN;
if (privateApiKey && bearerToken) {
  setFutureSecrets({
    privateApiKey,
    bearerToken
  });
}

declare global {
  interface Window {
    setFutureSecrets?: typeof setFutureSecrets;
  }
}

// since the token eventually expires, this is just a way in which to extend how long it works through developer intervention
if (window) {
  window.setFutureSecrets = setFutureSecrets;
}

// This implements the default behavior from styled-components v5
const shouldForwardProp = (propName: string, target: any) => {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
};

// Create QueryClient (shared between LandingPage and App)
const cache = new QueryCache({});
const queryClient = new QueryClient({
  queryCache: cache,
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      staleTime: Infinity,
      gcTime: Infinity
    }
  }
});

// Wrapper component that manages LandingPage and App
const AppRouter = () => {
  const enableSpacePlanning = isSpaceEnabled();
  const navigate = useNavigate();
  useEffect(() => {
    if (enableSpacePlanning) {
      navigate(AppRoutes.PLANNER, { replace: true });
    }
  }, [enableSpacePlanning, navigate]);
  const location = useLocation();
  const isReady = useIsReady();

  // Don't show landing page if URL had a VPC/design code (e.g., #Z25J9D)
  const isOnLandingPage = location.pathname === '/' || location.pathname === AppRoutes.LANDING_PAGE;

  // Track if we've ever loaded App (once loaded, keep it mounted)
  // In e2e mode, always load App to ensure PlannerDebug is available
  const [hasLoadedApp, setHasLoadedApp] = useState(() => !isOnLandingPage || e2e());

  useEffect(() => {
    // If we navigate away from root, load App
    if (!isOnLandingPage && !hasLoadedApp) {
      setHasLoadedApp(true);
    }
  }, [isOnLandingPage, hasLoadedApp]);

  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          {/* Web vitals reporter - runs on both landing page and planner */}
          <WebVitalsReporter />

          {/* Skeleton outside ServiceSettingsProvider to show while settings load */}
          {hasLoadedApp && !isOnLandingPage && (
            <StyleSheetManager shouldForwardProp={shouldForwardProp}>
              <PlannerSkeleton isReady={!isReady} />
            </StyleSheetManager>
          )}

          <ServiceSettingsProvider>
            <AppHelmet />
            <I18NProvider>
              <ServicesProvider>
                <VirtualKeyboardProvider>
                  {/* Keep landing page mounted once shown, just hide it when not on root */}
                  {isOnLandingPage && !enableSpacePlanning && (
                    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
                      <LandingPage />
                    </StyleSheetManager>
                  )}

                  {/* Load App when navigating away, keep it mounted but hidden when on root */}
                  {hasLoadedApp && (
                    <div style={{ display: isOnLandingPage ? 'none' : 'block' }}>
                      <StyleSheetManager shouldForwardProp={shouldForwardProp}>
                        <Suspense fallback={null}>
                          <Planner />
                        </Suspense>
                      </StyleSheetManager>
                    </div>
                  )}
                </VirtualKeyboardProvider>
              </ServicesProvider>
            </I18NProvider>
          </ServiceSettingsProvider>
        </ErrorBoundary>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

const container = document.getElementById('root')!;
const root = createRoot(container);

const router = createHashRouter(
  createRoutesFromElements(<Route path="/*" element={<AppRouter />} />)
);

root.render(<RouterProvider router={router} />);
